// Create new topic and invite users or send an invite.
import React from 'react';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';

import { Kaltiq } from 'kaltiq-sdk';

import ContactList from '../widgets/contact-list.jsx';
import NewTopicById from '../widgets/new-topic-by-id.jsx';
import NewTopicGroup from '../widgets/new-topic-group.jsx';
import SearchContacts from '../widgets/search-contacts.jsx';

import HashNavigation from '../lib/navigation.js';
import { formatICN, isValidICN, normalizeICN } from '../lib/kaltiq.js';
import { asEmail, asPhone, theCard } from '../lib/utils.js';

const messages = defineMessages({
  search_for_contacts: {
    id: "search_for_contacts",
    defaultMessage: "Use search to find contacts",
    description: "Text shown in contacts view when user entered no search query."
  },
  search_no_results: {
    id: "search_no_results",
    defaultMessage: "Search returned no results",
    description: "Text shown in contacts view when query returned no results."
  },
  search_placeholder: {
    id: "search_placeholder",
    defaultMessage: "Search by username, email ID, or 12-digit ICN number...",
    description: "Placeholder in contacts search field"
  },
});

const KEYPAD_KEYS = [
  { digit: '1', sub: 'ICN' },
  { digit: '2', sub: 'ABC' },
  { digit: '3', sub: 'DEF' },
  { digit: '4', sub: 'GHI' },
  { digit: '5', sub: 'JKL' },
  { digit: '6', sub: 'MNO' },
  { digit: '7', sub: 'PQRS' },
  { digit: '8', sub: 'TUV' },
  { digit: '9', sub: 'WXYZ' },
  { digit: 'CLR', sub: 'CLEAR' },
  { digit: '0', sub: '+' },
  { digit: 'DEL', sub: '⌫' },
];

class NewTopicView extends React.Component {
  constructor(props) {
    super(props);

    const urlParams = HashNavigation.parseUrlHash(window.location.hash).params;
    const initialTab = urlParams['tab'] || 'find';
    const initialQuery = initialTab == 'find' ? (urlParams['q'] || null) : null;

    this.state = {
      tabSelected: initialTab,
      searchQuery: initialQuery,
      dialDigits: '',
      dialBusy: false,
    };

    this.handleTabClick = this.handleTabClick.bind(this);
    this.handleSearchContacts = this.handleSearchContacts.bind(this);
    this.handleSearchResultSelected = this.handleSearchResultSelected.bind(this);
    this.handleNewGroupSubmit = this.handleNewGroupSubmit.bind(this);
    this.handleGroupByID = this.handleGroupByID.bind(this);
    this.handleKeypadPress = this.handleKeypadPress.bind(this);
    this.handleDialInputChange = this.handleDialInputChange.bind(this);
    this.handleDialAction = this.handleDialAction.bind(this);
  }

  componentDidMount() {
    this.props.onInitFind();
  }

  handleTabClick(e) {
    e.preventDefault();
    HashNavigation.navigateTo(HashNavigation.addUrlParam(window.location.hash, 'tab', e.currentTarget.dataset.id));
    this.setState({tabSelected: e.currentTarget.dataset.id});
  }

  handleSearchContacts(query) {
    const rawTrimmed = (query || '').trim();
    const digitsOnly = normalizeICN(rawTrimmed);
    if (isValidICN(digitsOnly) && /^[0-9\s-]+$/.test(rawTrimmed)) {
      query = `icn:${digitsOnly},${digitsOnly}`;
      this.props.onSearchContacts(query);
      this.setState({searchQuery: query});
      return;
    }

    query = rawTrimmed || Kaltiq.DEL_CHAR;
    if (!/[\s,:]/.test(query) && query != Kaltiq.DEL_CHAR) {
      const email = asEmail(query);
      if (email) {
        const lowerEmail = email.toLowerCase();
        query = `${Kaltiq.TAG_EMAIL}${lowerEmail},${lowerEmail}`;
      } else {
        let clean = query;
        if (clean[0] == '@') {
          clean = clean.substring(1);
        }
        clean = clean.toLowerCase();
        query = `${Kaltiq.TAG_ALIAS}${clean},serial:${clean},${clean}`;
      }
    }
    this.props.onSearchContacts(query);
    this.setState({searchQuery: Kaltiq.isNullValue(query) ? null : query});
  }

  handleSearchResultSelected(topicName) {
    if (this.state.tabSelected == 'find') {
      HashNavigation.navigateTo(HashNavigation.removeUrlParams(window.location.hash, ['tab', 'q']));
      this.props.onCreateTopic(topicName);
    }
  }

  handleNewGroupSubmit(name, description, dataUrl, priv, tags, isChannel) {
    HashNavigation.navigateTo(HashNavigation.removeUrlParams(window.location.hash, ['tab', 'q']));
    this.props.onCreateTopic(undefined,
      {public: theCard(name, dataUrl, null, description), private: priv, tags: tags}, isChannel);
  }

  handleGroupByID(rawInput) {
    let topicName = String(rawInput || '').trim();
    if (topicName.toLowerCase().startsWith('kaltiq:id/')) {
      topicName = topicName.slice('kaltiq:id/'.length).trim();
    }
    const hashIdx = topicName.lastIndexOf('#');
    if (hashIdx >= 0 && hashIdx + 1 < topicName.length) {
      topicName = topicName.slice(hashIdx + 1).trim();
    }
    if (!topicName) {
      return;
    }
    if ((topicName.startsWith('grp') || topicName.startsWith('chn')) && topicName.length > 5) {
      HashNavigation.navigateTo(HashNavigation.removeUrlParams(window.location.hash, ['tab', 'q']));
      this.props.onCreateTopic(topicName);
      return;
    }
    if (this.props.kaltiq) {
      this.props.kaltiq.lookup(topicName)
        .then(res => {
          if (res && res.user) {
            HashNavigation.navigateTo(HashNavigation.removeUrlParams(window.location.hash, ['tab', 'q']));
            this.props.onCreateTopic(res.user);
          } else if (topicName.startsWith('usr') && topicName.length > 5) {
            HashNavigation.navigateTo(HashNavigation.removeUrlParams(window.location.hash, ['tab', 'q']));
            this.props.onCreateTopic(topicName);
          } else {
            this.props.onError('User or number not found', 'err');
          }
        })
        .catch(err => {
          if (topicName.startsWith('usr') && topicName.length > 5) {
            HashNavigation.navigateTo(HashNavigation.removeUrlParams(window.location.hash, ['tab', 'q']));
            this.props.onCreateTopic(topicName);
          } else {
            this.props.onError(err.message || 'User or number not found', 'err');
          }
        });
      return;
    }
    HashNavigation.navigateTo(HashNavigation.removeUrlParams(window.location.hash, ['tab', 'q']));
    this.props.onCreateTopic(topicName);
  }

  handleKeypadPress(key) {
    if (key === 'CLR') {
      this.setState({dialDigits: ''});
    } else if (key === 'DEL') {
      this.setState(prev => ({dialDigits: prev.dialDigits.slice(0, -1)}));
    } else if (this.state.dialDigits.length < 12) {
      this.setState(prev => ({dialDigits: (prev.dialDigits + key).slice(0, 12)}));
    }
  }

  handleDialInputChange(e) {
    const digits = normalizeICN(e.target.value).slice(0, 12);
    this.setState({dialDigits: digits});
  }

  handleDialAction(mode) {
    const digits = normalizeICN(this.state.dialDigits);
    if (!isValidICN(digits)) {
      this.props.onError('Please enter a complete 12-digit KalTiQ number.', 'err');
      return;
    }
    if (!this.props.kaltiq) {
      this.props.onError('KalTiQ service not initialized.', 'err');
      return;
    }
    this.setState({dialBusy: true});
    this.props.kaltiq.lookup(digits)
      .then(res => {
        this.setState({dialBusy: false});
        if (!res || !res.user) {
          this.props.onError('No user assigned to this 12-digit number.', 'err');
          return;
        }
        HashNavigation.navigateTo(HashNavigation.removeUrlParams(window.location.hash, ['tab', 'q']));
        if (mode === 'audio' && this.props.onDialCall) {
          this.props.onDialCall(res.user, true);
        } else if (mode === 'video' && this.props.onDialCall) {
          this.props.onDialCall(res.user, false);
        } else {
          this.props.onCreateTopic(res.user);
        }
      })
      .catch(err => {
        this.setState({dialBusy: false});
        this.props.onError(err.message || 'Number not found', 'err');
      });
  }

  render() {
    const {formatMessage} = this.props.intl;
    const no_contacts_placeholder = formatMessage(this.state.searchQuery ?
      messages.search_no_results : messages.search_for_contacts);
    const search_placeholder = formatMessage(messages.search_placeholder);
    const formattedDial = formatICN(this.state.dialDigits);
    return (
      <div className="flex-column">
        <ul className="tabbar">
          <li className={this.state.tabSelected === 'find' ? 'active' : null}>
            <a href="#" data-id="find" onClick={this.handleTabClick}>
              <FormattedMessage id="tabtitle_find_user" defaultMessage="find"
                description="Tab title Find" />
            </a>
          </li>
          <li className={this.state.tabSelected === 'dial' ? 'active' : null}>
            <a href="#" data-id="dial" onClick={this.handleTabClick}>
              <FormattedMessage id="tabtitle_dialpad" defaultMessage="keypad"
                description="Tab title 12-digit Keypad Dialer" />
            </a>
          </li>
          <li className={this.state.tabSelected === 'grp' ? 'active' : null}>
            <a href="#" data-id="grp" onClick={this.handleTabClick}>
              <FormattedMessage id="tabtitle_new_group" defaultMessage="new group"
                description="Tab title New Group" />
            </a>
          </li>
          <li className={this.state.tabSelected === 'byid' ? 'active' : null}>
            <a href="#" data-id="byid" onClick={this.handleTabClick}>
              <FormattedMessage id="tabtitle_group_by_id" defaultMessage="by id"
                description="Tab title Find topic by ID" />
            </a>
          </li>
        </ul>
        {this.state.tabSelected === 'dial' ?
          <div className="kaltiq-dialpad-container">
            <div className="kaltiq-dialpad-screen">
              <div className="kaltiq-dialpad-label">12-DIGIT KALTIQ NUMBER (ICN)</div>
              <input
                type="text"
                className="kaltiq-dialpad-input"
                placeholder="0000 0000 0000"
                value={formattedDial}
                onChange={this.handleDialInputChange}
              />
              <div className="kaltiq-dialpad-counter">
                {this.state.dialDigits.length} / 12 digits
              </div>
            </div>
            <div className="kaltiq-dialpad-grid">
              {KEYPAD_KEYS.map(k => (
                <button
                  key={k.digit}
                  type="button"
                  className={`kaltiq-dialpad-key${k.digit === 'CLR' || k.digit === 'DEL' ? ' util-key' : ''}`}
                  onClick={() => this.handleKeypadPress(k.digit)}
                  disabled={this.state.dialBusy}
                >
                  <span className="key-main">{k.digit === 'DEL' ? '⌫' : k.digit}</span>
                  <span className="key-sub">{k.sub}</span>
                </button>
              ))}
            </div>
            <div className="kaltiq-dialpad-actions">
              <button
                type="button"
                className="kaltiq-dial-btn audio-call"
                disabled={this.state.dialBusy || this.state.dialDigits.length !== 12}
                onClick={() => this.handleDialAction('audio')}
                title="Voice Call"
              >
                <i className="material-icons">call</i>
                <span>Voice Call</span>
              </button>
              <button
                type="button"
                className="kaltiq-dial-btn video-call"
                disabled={this.state.dialBusy || this.state.dialDigits.length !== 12}
                onClick={() => this.handleDialAction('video')}
                title="Video Call"
              >
                <i className="material-icons">videocam</i>
                <span>Video Call</span>
              </button>
              <button
                type="button"
                className="kaltiq-dial-btn chat-msg"
                disabled={this.state.dialBusy || this.state.dialDigits.length !== 12}
                onClick={() => this.handleDialAction('chat')}
                title="Start Chat"
              >
                <i className="material-icons">chat</i>
                <span>Message</span>
              </button>
            </div>
          </div> :

        this.state.tabSelected === 'grp' ?
          <NewTopicGroup
            kaltiq={this.props.kaltiq}
            onSubmit={this.handleNewGroupSubmit}
            onError={this.props.onError} /> :

        this.state.tabSelected === 'byid' ?
          <NewTopicById
            myURI={'kaltiq:id/' + this.props.kaltiq.getCurrentUserID()}
            myUserId={this.props.kaltiq.getCurrentUserID()}
            onSubmit={this.handleGroupByID}
            onError={this.props.onError} /> :

          <div className="flex-column">
            <SearchContacts
              placeholder={search_placeholder}
              initialQuery={this.state.searchQuery || ''}
              onSearchContacts={this.handleSearchContacts} />
            <ContactList
              kaltiq={this.props.kaltiq}
              contacts={this.props.searchResults}
              myUserId={this.props.myUserId}
              emptyListMessage={no_contacts_placeholder}
              showSelfTopic={!this.state.searchQuery}
              showOnline={false}
              showUnread={false}
              showContextMenu={false}
              onTopicSelected={this.handleSearchResultSelected} />
          </div>}
      </div>
    );
  }
};

export default injectIntl(NewTopicView);
